import React, { useEffect, useState } from 'react'
import { Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, HStack, Button, Grid } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { useBlocker } from 'react-router-dom'
import './CategoriesActivities.css'
import { createCategoriesActivitiesApi } from './../../utils/api-interface'
import { CategoryListResponse, ConfirmActivitiesSuggestedList } from '../../services/network'
import { log } from '../../utils/logger'
import { SystemPage } from './SystemPage/SystemPage'
import { CreateCategoryModal } from '../../components/CreateCategoryModal/CreateCategoryModal'
import { CreateActivityModal } from '../../components/CreateActivityModal/CreateActivityModal'
import { ProposalPage } from './ProposalPage/ProposalPage'
import { DeleteConfirmationDialog } from '../../components/DeleteConfirmationDialog/DeleteConfirmationDialog'
import axios from 'axios'
import { RetryDialog } from '../../components/RetryDialog/RetryDialog'

function CategoriesActivities(): JSX.Element {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<CategoryListResponse>()
  const [selectedTab, setSelectedTab] = useState(0)
  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false)
  const [isCreateActivityModalOpen, setIsCreateActivityModalOpen] = useState(false)
  const [confirmSuggestedActivitiesList, setConfirmSuggestedActivitiesList] = useState<ConfirmActivitiesSuggestedList>()
  const [isDisabledTab, setIsDisabledTab] = useState(false)
  const [confirmDialogDisplayed, setConfirmDialogDisplayed] = useState(false)

  const [errorData, setErrorData] = useState<any>(null)
  const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)
  const handleCloseRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
  }

  useEffect(() => {
    eva.replace()
  }, [selectedTab])

  const fetchCategoriesActivities = async (): Promise<void> => {
    try {
      const categoriesActivitiesApi = createCategoriesActivitiesApi()
      const response = await categoriesActivitiesApi.getCategoriesActivities()
      log.debug(response)
      setCategories(response.data)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          log.error('Error fetching categories:', error.response.data)
          setErrorData(error.response.data)
        } else {
          log.error('No response from the server:', error.message)
        }
      } else {
        log.error('Unexpected error:', error)
      }
      setIsRetryDialogOpen(true)
    }
  }

  useEffect(() => {
    fetchCategoriesActivities()
  }, [])

  const handleConfirmRetryDialog = (): void => {
    setIsRetryDialogOpen(false)
    fetchCategoriesActivities()
  }

  const blocker = useBlocker(
    () => !!confirmSuggestedActivitiesList?.confirmActivitiesSuggested && confirmSuggestedActivitiesList.confirmActivitiesSuggested.length >= 1
  )

  // Function to handle tab change
  const handleTabChange = (tabIndex: number): void => {
    if (isDisabledTab) {
      setConfirmDialogDisplayed(true)
    } else {
      setSelectedTab(tabIndex)
    }
  }

  const handleDialogConfirm = (): void => {
    setConfirmSuggestedActivitiesList(undefined)
    setConfirmDialogDisplayed(false)
    setSelectedTab(0)
  }

  const handleDialogCancel = (): void => {
    setConfirmDialogDisplayed(false)
  }

  useEffect(() => {
    if (confirmSuggestedActivitiesList?.confirmActivitiesSuggested && confirmSuggestedActivitiesList?.confirmActivitiesSuggested.length >= 1) {
      setIsDisabledTab(true)
    } else {
      setIsDisabledTab(false)
    }
  }, [confirmSuggestedActivitiesList])

  useEffect(() => {
    eva.replace()
  }, [categories])

  return (
    <Grid
      templateRows={'auto 1fr'}
      bg={'greyNeutral100'}
      flexDirection='column'
      padding={'40px 120px 20px 40px'}
      gap={'30px'}
      overflow={'auto'}
      height={'100vh'}>
      <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left' marginTop={'30px'}>
        {t('categorization')}
      </Text>
      <Tabs index={selectedTab} onChange={handleTabChange} variant={'primary'} position={'relative'}>
        {confirmDialogDisplayed && (
          <DeleteConfirmationDialog
            isOpen={!!confirmSuggestedActivitiesList?.confirmActivitiesSuggested && confirmSuggestedActivitiesList?.confirmActivitiesSuggested.length >= 1}
            onClose={(): void => handleDialogCancel()}
            onConfirm={(): void => handleDialogConfirm()}
            type='blocker'
          />
        )}
        <HStack marginRight={'50px'} justifyContent={'space-between'}>
          <TabList>
            <Tab width='133px' className={selectedTab === 0 ? 'category-tab-selected' : 'userpage-tab'}>
              <Flex direction={'column'} alignItems={'center'} gap={'4px'}>
                <i data-eva={'archive-outline'} className='eva-icon'></i>
                <Text fontSize='14px' fontWeight='700' lineHeight='16px'>
                  {t('system')}
                </Text>
              </Flex>
            </Tab>
            <Tab className={selectedTab === 1 ? 'category-tab-selected' : 'userpage-tab'}>
              <Flex direction={'column'} alignItems={'center'} gap={'4px'}>
                <i data-eva={'paper-plane-outline'} className='eva-icon'></i>
                <Text fontSize='14px' fontWeight='700' lineHeight='16px'>
                  {t('proposals')}
                </Text>
              </Flex>
            </Tab>
          </TabList>
          {selectedTab === 0 && (
            <HStack spacing={4}>
              <Button
                variant={'outline'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
                textAlign={'center'}
                minWidth={'125px'}
                color={'primary.400'}
                borderColor={'primary.400'}
                onClick={(): void => setIsCreateCategoryModalOpen(true)}>
                {t('create_category')}
              </Button>
              <Button
                variant={'outline'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
                textAlign={'center'}
                minWidth={'125px'}
                color={'primary.400'}
                borderColor={'primary.400'}
                onClick={(): void => setIsCreateActivityModalOpen(true)}>
                {t('create_activity')}
              </Button>
            </HStack>
          )}
        </HStack>
        <TabPanels position={'absolute'} top={100} bottom={0} right={0} left={0}>
          <TabPanel position={'absolute'} top={0} bottom={0} right={0} left={0} overflow={'auto'}>
            {categories ? <SystemPage categories={categories?.categories} setCategories={setCategories}></SystemPage> : <></>}
          </TabPanel>
          <TabPanel position={'absolute'} top={0} bottom={0} right={0} left={0} overflow={'auto'}>
            <ProposalPage
              blocker={blocker}
              categories={categories ?? { categories: [] }}
              confirmSuggestedActivitiesList={confirmSuggestedActivitiesList}
              setConfirmSuggestedActivitiesList={setConfirmSuggestedActivitiesList}></ProposalPage>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <CreateCategoryModal
        isOpen={isCreateCategoryModalOpen}
        onClose={(): void => {
          setIsCreateCategoryModalOpen(false)
        }}
        categories={categories}
        setCategories={setCategories}
      />

      {categories?.categories ? (
        <CreateActivityModal
          isOpen={isCreateActivityModalOpen}
          onClose={(): void => {
            setIsCreateActivityModalOpen(false)
          }}
          categories={categories?.categories}
          setCategories={setCategories}
        />
      ) : (
        <></>
      )}
      <RetryDialog isOpen={isRetryDialogOpen} onClose={handleCloseRetryDialog} onConfirm={handleConfirmRetryDialog} message={errorData} />
    </Grid>
  )
}

export { CategoriesActivities }
